import type { ArchitectureProject } from '@hyphen-db-studio/architecture-core';
import { hasPermission, type MemberRole, type Permission } from '@hyphen-db-studio/access-core';
import { genId, type Project } from '@hyphen-db-studio/schema-core';
import { create } from 'zustand';
import { api } from '../lib/api';
import { clearLegacyData, loadLegacyData } from '../lib/persistence';
import { useArchitectureProjectsStore } from './useArchitectureProjectsStore';
import { useProjectsStore } from './useProjectsStore';

export type DiagramKind = 'er' | 'architecture';

/** An organization as seen by the signed-in user, including what that user is allowed to do in it. */
export interface Organization {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  role: MemberRole;
  permissions: Permission[];
}

/** A project groups ER (DB schema) diagrams and architecture diagrams inside an organization. */
export interface WorkspaceProject {
  id: string;
  orgId: string;
  name: string;
  description: string;
  erIds: string[];
  archIds: string[];
  createdAt: string;
  updatedAt: string;
}

interface BootstrapResponse {
  organizations: Organization[];
  projects: WorkspaceProject[];
  diagrams: { id: string; kind: DiagramKind; projectId: string; data: Project | ArchitectureProject }[];
}

interface WorkspaceState {
  organizations: Organization[];
  projects: WorkspaceProject[];
  /** True once the first server load after sign-in has finished. */
  hydrated: boolean;
  load: () => Promise<void>;
  reset: () => void;

  can: (orgId: string | null | undefined, permission: Permission) => boolean;
  canEditDiagram: (kind: DiagramKind, diagramId: string) => boolean;

  createOrganization: (name: string) => Promise<Organization>;
  removeOrganization: (id: string) => Promise<void>;
  leaveOrganization: (id: string, userId: string) => Promise<void>;

  createProject: (orgId: string, name: string) => Promise<WorkspaceProject>;
  removeProject: (id: string) => Promise<void>;

  /** Creates a diagram inside a project and returns its id. */
  createDiagram: (projectId: string, kind: DiagramKind, name: string, seedSample?: boolean) => Promise<string>;
  removeDiagram: (projectId: string, kind: DiagramKind, diagramId: string) => Promise<void>;
  findProjectOfDiagram: (kind: DiagramKind, diagramId: string) => WorkspaceProject | undefined;

  importLegacyData: () => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => {
  const dropLocalDiagrams = (p: WorkspaceProject) => {
    p.erIds.forEach((id) => useProjectsStore.getState().remove(id));
    p.archIds.forEach((id) => useArchitectureProjectsStore.getState().remove(id));
  };

  return {
    organizations: [],
    projects: [],
    hydrated: false,

    load: async () => {
      const data = await api.get<BootstrapResponse>('/api/bootstrap');
      useProjectsStore.getState().setAll(data.diagrams.filter((d) => d.kind === 'er').map((d) => d.data as Project));
      useArchitectureProjectsStore.getState().setAll(data.diagrams.filter((d) => d.kind === 'architecture').map((d) => d.data as ArchitectureProject));
      set({ organizations: data.organizations, projects: data.projects, hydrated: true });
    },

    reset: () => {
      useProjectsStore.getState().setAll([]);
      useArchitectureProjectsStore.getState().setAll([]);
      set({ organizations: [], projects: [], hydrated: false });
    },

    can: (orgId, permission) => {
      const org = get().organizations.find((o) => o.id === orgId);
      return !!org && hasPermission(org, permission);
    },

    canEditDiagram: (kind, diagramId) => {
      const project = get().findProjectOfDiagram(kind, diagramId);
      return get().can(project?.orgId, 'diagrams.edit');
    },

    createOrganization: async (name) => {
      const org = await api.post<Organization>('/api/orgs', { id: genId('org'), name });
      set({ organizations: [...get().organizations, org] });
      return org;
    },

    removeOrganization: async (id) => {
      await api.delete(`/api/orgs/${id}`);
      get()
        .projects.filter((p) => p.orgId === id)
        .forEach(dropLocalDiagrams);
      set({
        organizations: get().organizations.filter((o) => o.id !== id),
        projects: get().projects.filter((p) => p.orgId !== id),
      });
    },

    leaveOrganization: async (id, userId) => {
      await api.delete(`/api/orgs/${id}/members/${userId}`);
      get()
        .projects.filter((p) => p.orgId === id)
        .forEach(dropLocalDiagrams);
      set({
        organizations: get().organizations.filter((o) => o.id !== id),
        projects: get().projects.filter((p) => p.orgId !== id),
      });
    },

    createProject: async (orgId, name) => {
      const project = await api.post<WorkspaceProject>(`/api/orgs/${orgId}/projects`, { id: genId('wsp'), name });
      set({ projects: [...get().projects, project] });
      return project;
    },

    removeProject: async (id) => {
      await api.delete(`/api/projects/${id}`);
      const project = get().projects.find((p) => p.id === id);
      if (project) dropLocalDiagrams(project);
      set({ projects: get().projects.filter((p) => p.id !== id) });
    },

    createDiagram: async (projectId, kind, name, seedSample = false) => {
      const diagram: Project | ArchitectureProject =
        kind === 'er'
          ? useProjectsStore.getState().createNew(name, 'postgresql', seedSample)
          : useArchitectureProjectsStore.getState().createNew(name);
      const rollback = () =>
        kind === 'er' ? useProjectsStore.getState().remove(diagram.id) : useArchitectureProjectsStore.getState().remove(diagram.id);
      try {
        await api.post(`/api/projects/${projectId}/diagrams`, { id: diagram.id, kind, name: diagram.name, data: diagram });
      } catch (err) {
        rollback();
        throw err;
      }
      set({
        projects: get().projects.map((p) =>
          p.id === projectId ? (kind === 'er' ? { ...p, erIds: [...p.erIds, diagram.id] } : { ...p, archIds: [...p.archIds, diagram.id] }) : p,
        ),
      });
      return diagram.id;
    },

    removeDiagram: async (projectId, kind, diagramId) => {
      await api.delete(`/api/diagrams/${diagramId}`);
      if (kind === 'er') useProjectsStore.getState().remove(diagramId);
      else useArchitectureProjectsStore.getState().remove(diagramId);
      set({
        projects: get().projects.map((p) =>
          p.id === projectId
            ? kind === 'er'
              ? { ...p, erIds: p.erIds.filter((id) => id !== diagramId) }
              : { ...p, archIds: p.archIds.filter((id) => id !== diagramId) }
            : p,
        ),
      });
    },

    findProjectOfDiagram: (kind, diagramId) => get().projects.find((p) => (kind === 'er' ? p.erIds : p.archIds).includes(diagramId)),

    /** One-time upload of whatever this browser held before accounts existed; the signed-in user becomes the owner. */
    importLegacyData: async () => {
      const legacy = loadLegacyData();
      const orgIds = new Map<string, string>();
      for (const o of legacy.organizations) {
        orgIds.set(o.id, (await get().createOrganization(o.name)).id);
      }
      for (const wp of legacy.workspaceProjects) {
        const orgId = orgIds.get(wp.orgId);
        if (!orgId) continue;
        const project = await get().createProject(orgId, wp.name);
        for (const kind of ['er', 'architecture'] as const) {
          const ids = kind === 'er' ? wp.erIds : wp.archIds;
          const source: (Project | ArchitectureProject)[] = kind === 'er' ? legacy.er : legacy.architecture;
          for (const id of ids) {
            const diagram = source.find((d) => d.id === id);
            if (!diagram) continue;
            const newId = genId(kind === 'er' ? 'proj' : 'arch');
            const data = { ...diagram, id: newId };
            await api.post(`/api/projects/${project.id}/diagrams`, { id: newId, kind, name: diagram.name, data });
          }
        }
      }
      clearLegacyData();
      await get().load();
    },
  };
});
